
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { formatDistanceToNow } from "date-fns"
import type { ChatConversation } from "@/api/chat"
import { Loader2 } from "lucide-react"

interface ChatListProps {
  conversations: ChatConversation[]
  selectedConversation: string | null
  onSelectConversation: (id: string) => void
  isLoading?: boolean
}

export function ChatList({
  conversations,
  selectedConversation,
  onSelectConversation,
  isLoading,
}: ChatListProps) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (conversations.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        {t("chat.noConversations")}
      </div>
    )
  }

  return (
    <ScrollArea className="h-full">
      <div className="space-y-1 pr-4">
        {conversations.map((conversation) => (
          <div
            key={conversation._id}
            className={cn(
              "flex items-center justify-between p-3 rounded-md cursor-pointer hover:bg-muted transition-colors",
              selectedConversation === conversation._id && "bg-muted",
            )}
            onClick={() => onSelectConversation(conversation._id)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="font-medium truncate">{conversation.userName}</h4>
                {conversation.unreadByOwner > 0 && (
                  <Badge variant="default" className="ml-2 text-xs">
                    {conversation.unreadByOwner}
                  </Badge>
                )}
              </div>
              {conversation.lastMessage && (
                <p className="text-sm text-muted-foreground truncate mt-0.5">
                  {conversation.lastMessage}
                </p>
              )}
              {conversation.lastMessageAt && (
                <p className="text-xs text-muted-foreground mt-1">
                  {formatDistanceToNow(new Date(conversation.lastMessageAt), {
                    addSuffix: true,
                  })}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  )
}
